import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, useWindowDimensions, View, type ViewProps } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { type AddressType, Chip, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchTypePicker = memo((props: LocationSearchTypePickerProps) => {
  const { locationSearchType = '', isDisabled = false, onLocationSearchTypeChange } = props;
  const { formatMessage } = useIntl();
  const { width } = useWindowDimensions();
  const a11ySelector = useA11ySelector('locations.order-channel-picker.container'); // prettier-ignore

  // ─── Flags ───────────────────────────────────────────────────────────

  const isSmallScreen = width <= 375;

  // ─── Styles ──────────────────────────────────────────────────────────

  const chipStyles = [styles.chipReset, isSmallScreen ? styles.chipCompact : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View {...a11ySelector.props}>
      <Chip.Menu
        value={locationSearchType}
        onChange={onLocationSearchTypeChange}
        style={styles.menu}
      >
        {LocationSearchTypes.map((addressType) => {
          const isChecked = addressType === locationSearchType;

          return (
            <Chip
              value={addressType}
              key={addressType}
              disabled={isDisabled}
              accessibilityLabel={formatMessage(messages.locationSearchAddressType, {
                addressType,
              })}
              accessibilityState={{ checked: isChecked }}
              maxFontSizeMultiplier={1.2}
              palette="primary"
              testID={`search-bar.chip.${addressType}`}
              style={chipStyles}
            >
              {formatMessage(messages.locationSearchAddressType, {
                addressType,
              })}
            </Chip>
          );
        })}
      </Chip.Menu>
    </View>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationSearchAddressType: {
    defaultMessage: `{addressType, select,
    pickup {Pickup}
    outpost {Outpost}
    delivery {Delivery}
    other {Pickup}
  }`,
    description: 'Location Search | Address Type',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  menu: {
    justifyContent: 'center',
  },
  chipReset: {
    flex: undefined,
  },
  chipCompact: {
    paddingHorizontal: theme.spacing['3'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const LocationSearchTypes: readonly AddressType[] = ['pickup', 'delivery', 'outpost'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchTypePickerProps = Readonly<{
  locationSearchType?: string;
  isDisabled?: boolean;
  onLocationSearchTypeChange: (searchType: string) => void;
}> &
  Pick<ViewProps, 'testID'>;
